import { IncomingHttpHeaders } from 'http';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import createClient from '#providers/apollo/createClient';
import merge from 'deepmerge';
import isEqual from 'lodash/isEqual';
import isSSR from '#utils/is-ssr';

type InitialState = NormalizedCacheObject | undefined;

interface IInitializeApollo {
  headers?: IncomingHttpHeaders | null;
  initialState?: InitialState | null;
  context?: {
    resolvedUrl?: string;
    locale?: string;
  };
}

export let apolloClient: ApolloClient<NormalizedCacheObject>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const initializeClient = (
  { headers, initialState, context }: IInitializeApollo = {
    headers: null,
    initialState: null,
    context: null,
  }
) => {
  const _apolloClient = apolloClient ?? createClient(headers, context);

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // get hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();
    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray, sourceArray) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s))
        ),
      ],
    });
    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (isSSR()) return _apolloClient;

  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;
  //broadcast new queries to the client
  // @ts-expect-error: We're deliberately accessing a private property
  // It can probably be fixed via `apolloClient.reFetchObservableQueries()`
  apolloClient.queryManager.broadcastQueries();
  return apolloClient;
};

export const getApolloClient = () => {
  return apolloClient;
};

export default initializeClient;
